import './AmountPicker.scss';

import { mdiMinus, mdiPlus } from '@mdi/js';
import { InputNumber } from 'antd';
import Button from 'components/Button/Button';
import FlexRow from 'components/FlexRow/FlexRow';
import Icon from 'components/Icon/Icon';
import { showErrorMessage } from 'helpers/errorHandling';
import { MATERIAL_DECIMAL_PLACES } from 'helpers/materialHelpers';
import { mathRound } from 'helpers/mathHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

interface Props {
  value: number;
  maxAmount?: number | null;
  minAmount?: number;
  onChange: (value: number) => void;
  decimalPlaces?: number;
}

const AmountPicker: React.FC<Props> = (props): React.ReactElement => {
  const { onChange, maxAmount, minAmount, value, decimalPlaces } = props;

  const { t } = useCustomTranslation();

  const [internalValue, setInternalValue] = useState(value || 0);

  const handleAddClick = () => {
    const updatedValue = mathRound(value + 1, decimalPlaces || 2);

    if (maxAmount && updatedValue > maxAmount) {
      // TODO: change to warning
      showErrorMessage(t('customData.material.maxAmount'));
    } else if (maxAmount && updatedValue >= maxAmount) {
      onChange(maxAmount);
    } else {
      onChange(updatedValue);
    }
  };

  const handleRemoveClick = () => {
    const updatedValue = mathRound(value - 1, decimalPlaces || 2);

    if (minAmount && updatedValue <= minAmount) {
      // TODO: change to warning
      showErrorMessage(t('customData.material.minAmount'));
    } else if (minAmount !== undefined && updatedValue < minAmount) {
      onChange(minAmount);
    } else {
      onChange(updatedValue);
    }
  };

  const publishOnValueChanged = useDebouncedCallback(() => {
    onChange(internalValue);
  }, 500);

  const onValueChangedInternal = (value: number) => {
    setInternalValue(value);
    publishOnValueChanged();
  };

  return (
    <div className="AmountPicker">
      <FlexRow noMargin>
        <Button
          icon={<Icon path={mdiMinus} />}
          type="primary"
          onClick={() => handleRemoveClick()}
        />
        <InputNumber
          value={value}
          onChange={(value) => onValueChangedInternal(value || 0)}
          controls={false}
          decimalSeparator={t('misc.decimalSeparator')}
          precision={MATERIAL_DECIMAL_PLACES}
        />
        <Button icon={<Icon path={mdiPlus} />} type="primary" onClick={() => handleAddClick()} />
      </FlexRow>
    </div>
  );
};

export default AmountPicker;
